import { Component, OnDestroy, OnInit, QueryList, TemplateRef, ViewChildren } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
    BankDetails,
    bankingDetailsPath,
    Image,
    KeyValuePair,
    merchantParkingsPath,
    merchantsPath,
    Parking,
    parkingImagePath,
    ParkingLocation,
    ParkingStatus,
    ParkingType,
    Plan,
    Term,
} from '@parkupp/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { ParkingService } from '../../../services/parking.service'
import { doc, docSnapshots } from '@angular/fire/firestore'
import { BsModalService } from 'ngx-bootstrap/modal'
import * as uuid from 'uuid'
import * as firestore from 'firebase/firestore'
import { MerchantService } from '../../../services/merchant.service'
import { InputCurrencyDirective } from '../../../shared/core/directives/currency.directive'
import { ToastrService } from 'ngx-toastr'
import { AuthenticationService } from '../../../services/authentication.service'
import { Subscription } from 'rxjs'
import { SortImagesByDatePipe } from '../../../shared/core/pipes/sort-images-by-date.pipe'
import { AmenityDirective } from '../../../shared/core/directives/amenity.directive'
import * as moment from 'moment'
import { SortImagesByPrimaryImagePipe } from '../../../shared/core/pipes/sort-images-by-primary-image.pipe'
import Swal from 'sweetalert2'

@Component({
    selector: 'app-merchant-parking-create',
    templateUrl: './merchant-parking-create.component.html',
    styleUrls: ['./merchant-parking-create.component.scss'],
})
export class MerchantParkingCreateComponent implements OnInit, OnDestroy {
    @ViewChildren(InputCurrencyDirective) currencyDirectives: QueryList<InputCurrencyDirective>
    merchantKey: string
    docId: string
    formGroup: FormGroup
    disabled = true
    parking: Parking

    loading: boolean = false
    error: Error
    autoCompleteInputElement: any
    parkingLocation: ParkingLocation
    parkingsFilePath: string
    modalRef: any
    parkingImages: Image[] = []
    parkingImageFileName: string
    imageKey: string
    bankDetails: BankDetails
    editBankDetails: BankDetails
    parkingStatus = ParkingStatus
    returnUrl: string
    isValid = false
    undertake = false
    termsAndConditions = false
    mapHeight: any = 300 + 'px'
    imageSubscription: Subscription
    uploading = false
    agreeNotSelected = true
    amenitiesList: KeyValuePair[] = []
    typeSpecificAmenities: any = {}
    primaryImageUrl: string
    code: string
    message: string
    files: FileList
    removeImageKey: string

    serviceAgreementLoading: boolean = false
    leaseAgreementLoading: boolean = false
    // Plans
    availablePlans: Term[]
    showInputStartDate: boolean = false
    showInputLocationName: boolean = false
    showDailyDiscount: boolean = false
    readonly ParkingType = ParkingType

    constructor(
        private formBuilder: FormBuilder,
        private modalService: BsModalService,
        private activatedRoute: ActivatedRoute,
        private merchantService: MerchantService,
        private parkingService: ParkingService,
        public authService: AuthenticationService,
        private toastrService: ToastrService,
        private router: Router
    ) {
        this.merchantKey = this.activatedRoute.snapshot.params['merchantKey']
        this.returnUrl = router.url
        this.formGroup = this.formBuilder.group({
            parkingType: [{ value: '', disabled: true }, Validators.required],
            parkingLocation: ['', Validators.required],
            parkingLocationName: [''],
            title: ['', Validators.required],
            numberOfBays: ['', Validators.required],
            description: ['', Validators.required],
            parkerInformation: [''],
            term: ['anyTerm'],
            dailyFee: [''],
            monthlyFee: [''],
            onceOffFee: [''],
            dailySavings: [''],
            flexMonth: [''],
            flexHalfMonth: [''],
            startDate: [moment().format('yyyy-MM-DD')],
            admytLpr: [''],
            admytQr: [''],
            buildingName: [''],
            managerName: [''],
            whiteLabelName: [''],
            parkingAddressInstructions: [''],
            contactInformation: [''],
        })
    }

    public get Term() {
        return Term
    }

    ngOnInit(): void {
        this.activatedRoute.params.forEach((params) => {
            // const uid = params['merchantKey']
            const id = params['parkingKey']
            // this.merchantKey = uid
            this.docId = id
            this.registerImageListener(this.merchantKey, id)
            this.getParking(this.merchantKey, id)
        })
    }

    ngOnDestroy(): void {
        if (this.imageSubscription) {
            this.imageSubscription.unsubscribe()
        }
    }

    updateCurrencyDirectives() {
        for (const currencyDirective of this.currencyDirectives.toArray()) {
            currencyDirective.ngOnInit()
        }
    }

    registerImageListener(uid: string, id: string) {
        if (this.imageSubscription) {
            this.imageSubscription.unsubscribe()
        }
        this.imageSubscription = docSnapshots(doc(this.parkingService.firestore, merchantParkingsPath(uid), id)).subscribe((snapshot) => {
            if (snapshot.exists()) {
                const parking = Object.assign(new Parking(), snapshot.data())
                this.uploading = false
                this.parkingImages = []
                if (parking.images) {
                    for (const image of Object.values(parking.images)) {
                        this.parkingImages.push(image)
                    }
                }
                this.parkingImages = SortImagesByDatePipe.sortImages(this.parkingImages)
                if (this.parking) {
                    this.parking.images = parking.images
                    if (this.parkingImages) {
                        this.parkingImages = SortImagesByPrimaryImagePipe.sortImages(this.parkingImages, this.parking.imagePrimary ? this.parking.imagePrimary : '')
                    }
                }
                this.checkValidity()
            }
        })
    }

    getParking(uid: string, id: string) {
        const parkingSubscription = docSnapshots(doc(this.parkingService.firestore, merchantParkingsPath(uid), id)).subscribe((snapshot) => {
            if (snapshot.exists()) {
                this.parking = new Parking(snapshot.data())
                this.parking.$key = snapshot.id

                // Plan options
                if (this.parking.parkingType == ParkingType.PARKING_EVENTS) {
                    this.formGroup.controls['startDate'].setValidators([Validators.required])
                    if (this.parking.startDate) {
                        this.formGroup.controls['startDate'].setValue(this.parking.startDate)
                    }

                    this.showInputStartDate = true
                    this.showInputLocationName = true
                    this.availablePlans = [Term.ONCEOFF]
                } else if (this.parking.parkingType == ParkingType.FLEX) {
                    this.showInputLocationName = false
                    this.showInputStartDate = false
                    this.showDailyDiscount = true
                    this.availablePlans = [Term.FLEX_MONTH, Term.FLEX_HALF_MONTH]
                } else {
                    this.showInputStartDate = false
                    this.showInputLocationName = false
                    this.showDailyDiscount = true
                    this.availablePlans = [Term.MONTHLY]
                }

                if (this.parking.bankDetailsRef) {
                    this.parking.loadBankDetails(firestore).then(() => {
                        this.bankDetails = this.parking.$bankDetails!
                    })
                }

                if (this.parking.parkingLocation && this.parking.parkingLocation.city) {
                    this.parkingLocation = this.parking.parkingLocation
                    this.formGroup.patchValue({
                        parkingLocation: `${this.parking.parkingLocation.streetNumber} ${this.parking.parkingLocation.streetAddress}, ${this.parking.parkingLocation.suburb}, ${this.parking.parkingLocation.city}, ${this.parking.parkingLocation.postalCode}, ${this.parking.parkingLocation.country}`,
                    })
                }

                if (this.parking.plans) {
                    this.parking.plans.forEach((plan) => {
                        if (plan.term == Term.DAILY) {
                            this.formGroup.patchValue({ dailyFee: plan.feeInCents / 100 })
                        }
                        if (plan.term == Term.MONTHLY) {
                            this.formGroup.patchValue({ monthlyFee: plan.feeInCents / 100 })
                        }
                        if (plan.term == Term.ONCEOFF) {
                            this.formGroup.patchValue({ onceOffFee: plan.feeInCents / 100 })
                        }
                        if (plan.term == Term.FLEX_MONTH) {
                            this.formGroup.patchValue({ flexMonth: plan.feeInCents / 100 })
                        }
                        if (plan.term == Term.FLEX_HALF_MONTH) {
                            this.formGroup.patchValue({ flexHalfMonth: plan.feeInCents / 100 })
                        }
                    })
                }

                if (this.parking.dailySavings) {
                    this.formGroup.patchValue({ dailySavings: this.parking.dailySavings / 100 })
                }

                if (this.parking.whiteLabelName) {
                    this.formGroup.patchValue({ whiteLabelName: this.parking.whiteLabelName.toLowerCase() })
                }

                this.formGroup.patchValue({
                    title: this.parking.title,
                    numberOfBays: this.parking.numberOfBays,
                    description: this.parking.description,
                    parkerInformation: this.parking.parkerInformation,
                    parkingType: this.parking.parkingType,
                    parkingLocationName: this.parking.parkingLocationName,
                    admytLpr: this.parking.admytLpr,
                    admytQr: this.parking.admytQr,
                    buildingName: this.parking.buildingName,
                    managerName: this.parking.managerName,
                    parkingAddressInstructions: this.parking.parkingAddressInstructions,
                    contactInformation: this.parking.contactInformation,
                })

                this.updateCurrencyDirectives()

                this.checkValidity()
                this.updateAmenities()
            }
            parkingSubscription.unsubscribe()
            setTimeout(() => {
                this.autoCompleteInputElement = document.getElementById('parkingLocation')
                this.fileUpload()
            })
        })
    }

    checkValidity() {
        if (this.parking && this.parking.title && this.parking.description && this.parking.parkingLocation && this.parking.images && this.parking.plans) {
            this.isValid = true
        } else {
            this.isValid = false
        }
    }

    onSave() {
        this.save()
            .then(() => {
                this.toastrService.success('Parking saved successfully', 'Success')
                this.loading = false
            })
            .catch((error) => {
                this.error = error['message']
                this.loading = false
            })
    }

    save(): Promise<void> {
        this.loading = true

        let amenityList: KeyValuePair[] = []

        // amenityList.push(
        //     new KeyValuePair(this.formGroup.value.nightLighting, 'nightLighting'),
        //     new KeyValuePair(this.formGroup.value.onSiteGuard, 'onSiteGuard'),
        //     new KeyValuePair(this.formGroup.value.ccTv, 'ccTv'),
        //     new KeyValuePair(this.formGroup.value.controlledAccess, 'controlledAccess'),
        //     new KeyValuePair(this.formGroup.value.coveredParking, 'coveredParking'),
        //     new KeyValuePair(this.formGroup.value.underground, 'underground'),
        //     new KeyValuePair(this.formGroup.value.maxSize, 'maxSize')
        // )

        for (const amenity of this.amenitiesList) {
            amenityList.push(new KeyValuePair((this.formGroup.get(amenity.key)?.value === true) as any, amenity.key))
        }

        let plans: Plan[] = []
        if (this.formGroup.value.dailyFee) {
            plans.push(
                new Plan({
                    feeInCents: this.formGroup.value.dailyFee * 100,
                    term: Term.DAILY,
                })
            )
        }
        if (this.formGroup.value.monthlyFee) {
            plans.push(
                new Plan({
                    feeInCents: this.formGroup.value.monthlyFee * 100,
                    term: Term.MONTHLY,
                })
            )
        }
        if (this.formGroup.value.onceOffFee) {
            plans.push(
                new Plan({
                    feeInCents: this.formGroup.value.onceOffFee * 100,
                    term: Term.ONCEOFF,
                })
            )
        }
        if (this.formGroup.value.flexMonth) {
            plans.push(
                new Plan({
                    feeInCents: this.formGroup.value.flexMonth * 100,
                    term: Term.FLEX_MONTH,
                })
            )
        }
        if (this.formGroup.value.flexHalfMonth) {
            plans.push(
                new Plan({
                    feeInCents: this.formGroup.value.flexHalfMonth * 100,
                    term: Term.FLEX_HALF_MONTH,
                })
            )
        }

        if (this.formGroup.value.dailySavings) {
            this.parking.dailySavings = this.formGroup.value.dailySavings * 100
        }

        this.parking.parkingType = this.formGroup.get('parkingType')?.value
        if (this.parkingLocation) {
            this.parking.parkingLocation = this.parkingLocation
        } else {
            this.parking.parkingLocation = {} as any
        }
        this.parking.parkingLocationName = this.formGroup.value.parkingLocationName ? this.formGroup.value.parkingLocationName : ''

        this.parking.title = this.formGroup.value.title ? this.formGroup.value.title : ''
        this.parking.numberOfBays = this.formGroup.value.numberOfBays ? this.formGroup.value.numberOfBays : ''
        this.parking.description = this.formGroup.value.description ? this.formGroup.value.description : ''
        this.parking.parkerInformation = this.formGroup.value.parkerInformation ? this.formGroup.value.parkerInformation : ''
        this.parking.parkingAmenities = JSON.parse(JSON.stringify(amenityList))
        this.parking.plans = plans
        this.parking.merchantRef = firestore.doc(this.merchantService.firestore, merchantsPath(), this.merchantKey) as any
        this.parking.imagePrimary = this.primaryImageUrl

        this.parking.admytLpr = this.formGroup.value.admytLpr
        this.parking.admytQr = this.formGroup.value.admytQr

        this.parking.parkingAddressInstructions = this.formGroup.value.parkingAddressInstructions
        this.parking.contactInformation = this.formGroup.value.contactInformation
        // White label
        this.parking.managerName = this.formGroup.value.managerName
        this.parking.whiteLabelName = this.formGroup.value.whiteLabelName
        this.parking.buildingName = this.formGroup.value.buildingName

        if (this.parking.parkingType == ParkingType.PARKING_EVENTS) {
            this.parking.startDate = this.formGroup.value.startDate
        }

        let parking = this.parking.parse()
        return this.parkingService.updateMerchantParking(parking, this.merchantKey, this.parking.$key!)
    }

    onChangeLocation(parkingLocation: ParkingLocation) {
        this.parkingLocation = parkingLocation
    }

    uploadComplete() {
        this.uploading = true
        this.closeSaveFormModal()
    }

    closeSaveFormModal(bankDetails?: BankDetails) {
        if (bankDetails) {
            this.bankDetails = bankDetails
            this.parking.bankDetailsRef = firestore.doc(this.parkingService.firestore, bankingDetailsPath(this.merchantKey), bankDetails.$key!.toString()) as any
        }
        this.modalRef?.hide()
    }

    openImageModal(template: TemplateRef<any>) {
        this.imageKey = uuid.v4()
        this.parkingsFilePath = parkingImagePath(this.merchantKey, this.docId, this.imageKey)
        this.modalRef = this.modalService.show(template, { class: 'modal-lg' })
    }

    openBankDetailsModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template)
    }

    openBankDetailsFormModal(template: TemplateRef<any>, bankDetails: BankDetails) {
        this.editBankDetails = bankDetails
        this.modalRef?.hide()
        this.modalRef = this.modalService.show(template)
    }

    openDeleteModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template)
    }

    openPublishModal(template: TemplateRef<any>) {
        this.save()
            .then(() => {
                this.modalRef = this.modalService.show(template)
                this.loading = false
            })
            .catch(() => {
                this.loading = false
            })
    }

    openAdminPublishModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template)
    }

    preview() {
        const url = this.router.serializeUrl(this.router.createUrlTree([`parking/${this.parking.$key}`]))
        window.open(url, '_blank')
    }

    addBankDetails(template: TemplateRef<any>) {
        this.openBankDetailsFormModal(template, new BankDetails())
    }

    bankDetailsSelected(bankDetails: BankDetails) {
        this.bankDetails = bankDetails
        this.parking.bankDetailsRef = firestore.doc(this.parkingService.firestore, bankingDetailsPath(this.merchantKey), bankDetails.$key!.toString()) as any
        this.closeSaveFormModal()
    }

    onTermSelected(event: any) {
        if (event.target.value == 'daily') {
            setTimeout(() => {
                // document.getElementById('dailyFee')!!.hidden = false
                document.getElementById('monthlyFee')!!.hidden = true
            })
            this.formGroup.controls['monthlyFee'].setValue(null)
        } else if (event.target.value == 'monthly') {
            setTimeout(() => {
                // document.getElementById('dailyFee')!!.hidden = true
                document.getElementById('monthlyFee')!!.hidden = false
            })
            this.formGroup.controls['dailyFee'].setValue(null)
        } else {
            setTimeout(() => {
                // document.getElementById('dailyFee')!!.hidden = false
                document.getElementById('monthlyFee')!!.hidden = false
            })
        }
    }

    archiveParking() {
        this.parking.parkingStatus = this.parkingStatus.DISABLED
        this.save()
            .then(() => {
                this.modalRef?.hide()
                this.toastrService.success('Parking deleted successfully', 'Deleted')
                this.loading = false
                this.router.navigate([`/merchant/${this.merchantKey}`])
            })
            .catch((error) => {
                this.error = error['message']
                this.loading = false
            })
    }

    publishForReview() {
        this.agreeNotSelected = true
        if (!this.undertake || !this.termsAndConditions) {
            this.agreeNotSelected = false
            return
        }
        this.parking.parkingStatus = this.parkingStatus.PENDING
        this.save()
            .then(() => {
                this.modalRef?.hide()
                this.toastrService.success('Parking has been published for review', 'Successful')
                this.loading = false
            })
            .catch((error) => {
                this.error = error['message']
                this.loading = false
            })
    }

    approveForParking() {
        this.parking.parkingStatus = this.parkingStatus.PUBLISHED
        this.save()
            .then(() => {
                this.modalRef?.hide()
                this.toastrService.success('Parking has been approved', 'Successful')
                this.loading = false
            })
            .catch((error) => {
                this.error = error['message']
                this.loading = false
            })
    }

    confirmDeleteImage() {
        this.parkingService.deleteParkingImage(this.merchantKey, this.parking.$key!!, this.removeImageKey, this.parking.images!).then(() => {
            this.modalRef?.hide()
        })
    }

    deleteImage(image: Image, template: TemplateRef<any>) {
        for (let imagesKey in this.parking.images) {
            if (image.smallUrl == this.parking.images[imagesKey].smallUrl) {
                this.removeImageKey = imagesKey
            }
        }
        this.modalRef = this.modalService.show(template)
        return false
    }

    makeImagePrimary(image: Image) {
        for (let imagesKey in this.parking.images) {
            if (image.originalUrl == this.parking.images[imagesKey].originalUrl) {
                this.primaryImageUrl = this.parking.images[imagesKey].originalUrl
            }
        }
        return false
    }

    undertakeCheckboxListener(event: any) {
        this.undertake = event.target.checked
    }

    termsCheckboxListener(event: any) {
        this.termsAndConditions = event.target.checked
    }

    updateAmenities() {
        this.amenitiesList.forEach((amenity) => this.formGroup.removeControl(amenity.key))
        this.amenitiesList = []

        this.typeSpecificAmenities = AmenityDirective.amenities(this.parking.parkingType)
        for (const key in this.typeSpecificAmenities) {
            this.amenitiesList.push(new KeyValuePair(this.getAmenityValue(key), key))
        }

        this.amenitiesList.forEach((amenity) => this.formGroup.addControl(amenity.key, new FormControl(amenity.value)))
    }

    getAmenityValue(key: string): any {
        if (this.parking.parkingAmenities) {
            for (const amenity of this.parking.parkingAmenities) {
                if (amenity.key === key) {
                    return amenity.value
                }
            }
        }
        return false
    }

    fileUpload(): void {
        const $inputImage = document.getElementById('inputFile')
        const that = this
        if ($inputImage) {
            $inputImage.addEventListener('change', (event) => {
                if (!(event.target as any).files.length) {
                    return
                }
            })
        }
    }

    async getDescriptionHelp() {
        await Swal.fire({
            title: 'Example Description',
            text: 'This parking bay is located in a secure, well-lit area with 24/7 surveillance. It is conveniently situated near major highways and public transport, making it ideal for daily commuters. The bay is spacious enough to accommodate vehicles of all sizes, including SUVs and trucks. Additionally, it is within walking distance to several amenities such as cafes, shops, and offices.',
        })
    }

    async selectAndUploadContract(event: any) {
        this.parking.usePolicyUrl = null
        const files = event.target.files

        if (files.length === 0) {
            return
        }

        const contractFile = files[0]

        this.serviceAgreementLoading = true

        const uploadedFileUrl = await this.parkingService.uploadServiceAgreement(this.merchantKey, this.parking.$key!, contractFile)

        this.parking.usePolicyUrl = uploadedFileUrl
        console.log({ uploadedFileUrl, parkingKey: this.parking.$key, merchantKey: this.merchantKey })
        await this.parkingService.updateMerchantParking({ usePolicyUrl: uploadedFileUrl }, this.merchantKey, this.parking.$key!)
        this.serviceAgreementLoading = false
        console.log('uploadedFileUrl', uploadedFileUrl)
    }

    async selectAndUploadLeaseAgreement(event: any) {
        this.parking.leaseAgreementUrl = null
        const files = event.target.files

        if (files.length === 0) {
            return
        }

        const leaseAgreementFile = files[0]

        this.leaseAgreementLoading = true

        const uploadedFileUrl = await this.parkingService.uploadLeaseAgreement(this.merchantKey, this.parking.$key!, leaseAgreementFile)

        this.parking.leaseAgreementUrl = uploadedFileUrl
        console.log({ uploadedFileUrl, parkingKey: this.parking.$key, merchantKey: this.merchantKey })
        await this.parkingService.updateMerchantParking({ leaseAgreementUrl: uploadedFileUrl }, this.merchantKey, this.parking.$key!)
        this.leaseAgreementLoading = false
        console.log('uploadedFileUrl', uploadedFileUrl)
    }
}
